import React, { useState, useEffect } from "react";
import {
  Edit2,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ImageIcon,
  Upload,
} from "lucide-react";
import Select from "react-select";
import "./ProductDetails.css";
import axios from "axios";
import { toast } from "react-toastify";

const ProductDetails = () => {
  const API_URL = "https://payyug.in/api";
  const superAdminId = localStorage.getItem("adminuserId");

  const initialFormState = {
    id: null,
    category: null,
    brand: null,
    model: null,
    variant: null,
    productName: "",
    productPrice: "",
    discountPrice: "",
    healthInsurance: "",
    theftDamageAmt: "",
    extWarranty: "",
    futureCard: "",
    stockQuantity: "",
    image: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [imagePreview, setImagePreview] = useState(null);
  const [productDetails, setProductDetails] = useState([]);
  const [fetchedData, setFetchedData] = useState({
    categories: [],
    brands: [],
    models: [],
    variants: [],
  });

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  // ✅ IMAGE URL FIX
  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith("http")) return img;
    // return `${API_URL}/${img}`;
    return `https://payyug.in/uploads/${img}`;
  };

  // ✅ FIND SELECT OPTION
  const findOption = (options, id) => {
    return options.find((opt) => opt.value === id) || null;
  };

  // FETCH MASTER DATA
  const fetchAllDataOnce = async () => {
    try {
      const [catRes, brandRes, modelRes, variantRes] = await Promise.all([
        axios.get(`${API_URL}/product-loan/category/list`, {
          params: { superAdminId, limit: 1000 },
        }),
        axios.get(`${API_URL}/product-loan/brand/list`, {
          params: { superAdminId, limit: 1000 },
        }),
        axios.get(`${API_URL}/product-loan/model/list`, {
          params: { superAdminId, limit: 1000 },
        }),
        axios.get(`${API_URL}/product-loan/variant/list`, {
          params: { superAdminId, limit: 1000 },
        }),
      ]);

      setFetchedData({
        categories:
          catRes.data.data?.map((i) => ({
            value: i.id,
            label: i.name,
          })) || [],
        brands:
          brandRes.data.data?.map((i) => ({
            value: i.id,
            label: i.name,
          })) || [],
        models:
          modelRes.data.data?.map((i) => ({
            value: i.id,
            label: i.name,
          })) || [],
        variants:
          variantRes.data.data?.map((i) => ({
            value: i.id,
            label: i.name,
          })) || [],
      });
    } catch (err) {
      toast.error("Failed to fetch master data");
    }
  };

  // FETCH PRODUCTS
  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/product-loan/product/list`, {
        params: {
          superAdminId,
          page: currentPage,
          limit: itemsPerPage,
        },
      });

      if (res.data.success) {
        setProductDetails(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalRecords(res.data.totalRecords || 0);
      }
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDataOnce();
  }, []);

  useEffect(() => {
    fetchProductDetails();
  }, [currentPage, itemsPerPage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setImagePreview(null);
    setIsEditing(false);

    const fileInput = document.getElementById("imageInput");
    if (fileInput) fileInput.value = "";
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();

    data.append("superAdminId", superAdminId);
    data.append("categoryId", formData.category?.value || "");
    data.append("brandId", formData.brand?.value || "");
    data.append("modelId", formData.model?.value || "");
    data.append("variantId", formData.variant?.value || "");
    data.append("productName", formData.productName);
    data.append("productPrice", formData.productPrice);
    data.append("discountPrice", formData.discountPrice);
    data.append("healthInsurance", formData.healthInsurance || "");
    data.append("theftDamageAmt", formData.theftDamageAmt || "");
    data.append("extWarranty", formData.extWarranty || "");
    data.append("futureCard", formData.futureCard || "");
    data.append("stockQuantity", formData.stockQuantity);

    // ✅ IMAGE FIX
    if (formData.image instanceof File) {
      data.append("image", formData.image);
    } else if (typeof formData.image === "string") {
      data.append("image", formData.image);
    }

    try {
      let res;

      if (isEditing) {
        res = await axios.put(
          `${API_URL}/product-loan/product/update/${formData.id}`,
          data,
        );
      } else {
        res = await axios.post(`${API_URL}/product-loan/product/create`, data);
      }

      if (res.data.success) {
        toast.success(res.data.message || "Saved Successfully!");
        handleReset();
        fetchProductDetails();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const res = await axios.delete(
          `${API_URL}/product-loan/product/delete/${id}`,
        );

        toast.success(res.data.message || "Deleted successfully!");
        fetchProductDetails();
      } catch (err) {
        toast.error(err?.response?.data?.message || "Delete failed");
      }
    }
  };

  // ✅ EDIT FIX
  const handleEditClick = (item) => {
    setIsEditing(true);

    setFormData({
      id: item.id,
      productName: item.productName || "",
      productPrice: item.productPrice || "",
      discountPrice: item.discountPrice || "",
      healthInsurance: item.healthInsurance || "",
      theftDamageAmt: item.theftDamageAmt || "",
      extWarranty: item.extWarranty || "",
      futureCard: item.futureCard || "",
      stockQuantity: item.stockQuantity || "",

      category: findOption(fetchedData.categories, item.categoryId),
      brand: findOption(fetchedData.brands, item.brandId),
      model: findOption(fetchedData.models, item.modelId),
      variant: findOption(fetchedData.variants, item.variantId),

      image: item.image,
    });

    setImagePreview(item.image ? getImageUrl(item.image) : null);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filteredItems = productDetails.filter((v) =>
    (v.productName || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="product-master-container">
      <div className="product-master-header-card">
        <h2>{isEditing ? "Edit Product" : "Create New Product"}</h2>
        <button className="reset-btn" onClick={handleReset}>
          <RefreshCw size={14} /> Reset Form
        </button>
      </div>

      {/* FORM SECTION */}
      <div className="product-master-form-card">
        <form onSubmit={handleSubmit}>
          <div className="product-master-form-grid">
            <div className="product-master-form-input-group">
              <label>Category</label>
              <Select
                className="product-master-Select"
                classNamePrefix={"react-select"}
                options={fetchedData.categories}
                value={formData.category}
                onChange={(s) => setFormData({ ...formData, category: s })}
                placeholder="Select..."
                isSearchable
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Brand</label>
              <Select
                className="product-master-Select"
                classNamePrefix={"react-select"}
                options={fetchedData.brands}
                value={formData.brand}
                onChange={(s) => setFormData({ ...formData, brand: s })}
                placeholder="Select..."
                isSearchable
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Model</label>
              <Select
                className="product-master-Select"
                classNamePrefix={"react-select"}
                options={fetchedData.models}
                value={formData.model}
                onChange={(s) => setFormData({ ...formData, model: s })}
                placeholder="Select..."
                isSearchable
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Variant</label>
              <Select
                className="product-master-Select"
                classNamePrefix={"react-select"}
                options={fetchedData.variants}
                value={formData.variant}
                onChange={(s) => setFormData({ ...formData, variant: s })}
                placeholder="Select..."
                isSearchable
              />
            </div>

            <div className="product-master-form-input-group">
              <label>Product Name</label>
              <input
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Product Price</label>
              <input
                type="number"
                name="productPrice"
                value={formData.productPrice}
                onChange={handleChange}
                required
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Discount Price</label>
              <input
                type="number"
                name="discountPrice"
                value={formData.discountPrice}
                onChange={handleChange}
                required
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Stock Qty</label>
              <input
                type="number"
                name="stockQuantity"
                value={formData.stockQuantity}
                onChange={handleChange}
                required
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Health Insurance</label>
              <input
                name="healthInsurance"
                value={formData.healthInsurance}
                onChange={handleChange}
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Theft Damage Amt</label>
              <input
                name="theftDamageAmt"
                value={formData.theftDamageAmt}
                onChange={handleChange}
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Ext. Warranty</label>
              <input
                name="extWarranty"
                value={formData.extWarranty}
                onChange={handleChange}
              />
            </div>
            <div className="product-master-form-input-group">
              <label>Future Card</label>
              <input
                name="futureCard"
                value={formData.futureCard}
                onChange={handleChange}
              />
            </div>

            <div className="product-master-form-input-group image-upload-group">
              <label>Product Image</label>
              <div className="image-upload-wrapper">
                <input
                  type="file"
                  id="imageInput"
                  accept="image/*"
                  onChange={handleImageChange}
                  hidden
                />
                <label htmlFor="imageInput" className="image-label-button">
                  <Upload size={16} /> Choose Image
                </label>
                {imagePreview ? (
                  <div className="image-preview-box">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                ) : (
                  <div className="image-placeholder">
                    <ImageIcon size={30} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button type="submit" className="product-master-form-isubmin-btn">
              {isEditing ? "Update Product" : "Save Product"}
            </button>
          </div>
        </form>
      </div>

      {/* TABLE SECTION */}
      <div className="product-master-table-container">
        <div className="product-master-table-controles">
          <div>
            Show
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>{" "}
            entries
          </div>
          <div className="product-master-table-search-box">
            <Search
              size={16}
              style={{ position: "absolute", left: "10px", top: "10px" }}
            />
            <input
              style={{ paddingLeft: "35px" }}
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="product-master-table-report">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Image</th>
                <th>Product Name</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Stock</th>
                <th>Insurance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((s, index) => (
                  <tr key={s.id || index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      {s.image ? (
                        <img
                          src={getImageUrl(s.image)}
                          alt="prod"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <ImageIcon size={20} color="#ccc" />
                      )}
                    </td>
                    <td style={{ fontWeight: "600" }}>{s.productName}</td>
                    <td>₹{s.productPrice}</td>
                    <td style={{ color: "green" }}>₹{s.discountPrice}</td>
                    <td>{s.stockQuantity}</td>
                    <td>{s.healthInsurance || "N/A"}</td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditClick(s)}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(s.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="product-master-pagination">
          <div>
            Showing {filteredItems.length} of {totalRecords} entries
          </div>
          <div className="pagination-buttons">
            <button
              className="product-master-pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft size={14} />
            </button>
            <button className="product-master-pagination-btn active">
              {currentPage}
            </button>
            <button
              className="product-master-pagination-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
